<template>
  <v-container class="main-card mt-2">
    <v-row>
      <v-col cols="12">
        <div class="text-overline">Reviewer dashboard</div>
      </v-col>
      <v-col cols="12" lg="8" md="8">
        <v-row>
          <v-col cols="12">
            <v-skeleton-loader v-if="profileLoading" type="card"></v-skeleton-loader>
            <div v-if="!profileLoading">
              <v-card v-if="profileErrorMsg">
                <v-card-text>
                  <span class="red--text ma-0 sa-0">{{ profileErrorMsg }}</span>
                </v-card-text>
              </v-card>
              <v-card v-if="!profileErrorMsg" elevation="1">
                <v-card-title class="mb-1 text-h5 d-flex justify-space-between">
                  <div>
                    <v-avatar color="orange" size="45" class="mr-3">
                      <span class="white--text text-h5">
                        {{ revProfile.PublicName.charAt(0) }}
                      </span>
                    </v-avatar>
                    {{ revProfile.PublicName }}
                  </div>
                  <div>
                    <v-chip
                      :color="
                        revProfile.Status === 'active' ? 'green' : 'orange'
                      "
                      text-color="white"
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ revProfile.Status }}
                    </v-chip>
                  </div>
                </v-card-title>
                <v-card-subtitle>Joined 11/17/2024</v-card-subtitle>
                <v-card-text>
                  <a :href="`/rev-profile?id=${revProfile.Id}`" class="text-decoration-none">
                    View your public profile
                  </a>
                </v-card-text>
            </v-card>
            </div>
            
          </v-col>
          <v-col cols="12">
            <v-card>
              <v-card-title class="pb-2 pt-2 text-overline">
                Reviews assigned to you
              </v-card-title>
              <v-card-text>
                <div v-if="assignedRevLoading" >
                  <v-skeleton-loader type="table-heading"></v-skeleton-loader> 
                  <v-skeleton-loader type="list-item"></v-skeleton-loader>
                </div>
                <div v-if="!assignedRevLoading">
                  <div v-if="assignedRevErrorMsg">
                    <span class="red--text ma-0 sa-0">{{ assignedRevErrorMsg }}</span>
                    <v-btn @click="getAssignedReviews" icon color="primary">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </div>
                  <div v-if="!assignedRevErrorMsg">
                    <div
                      v-if="assignedItems.length == 0"
                      class="mb-2 d-flex justify-center text-caption"
                    >
                    You don't have any reviews assigned to you yet
                    </div>
                    <v-data-table
                      :headers="computedAssignedHeaders"
                      :items="assignedItems"
                      :items-per-page="5"
                      hide-default-footer
                      class="elevation-1"
                      v-if="assignedItems.length > 0"
                    >
                      <template v-slot:item.Tier="{ item }">
                        <v-chip
                            :color="$consts.TIER_MAP.get(item.Tier).color"
                            text-color="white"
                            small
                          >
                          {{ $consts.TIER_MAP.get(item.Tier).name }}
                        </v-chip>
                      </template>
                      <template v-slot:item.LastActionTs="{ item }">
                        {{ $utils.relativeTimeFromTs(item.LastActionTs) }}
                      </template>
                      <template v-slot:item.Status="{ item }">
                        <v-icon color="orange" x-small class="mr-1">
                          mdi-circle
                        </v-icon>
                        <span class="d-inline">{{
                          item.Status === "inreview" ? "Pending" : "unknown"
                        }} </span>
                      </template>
                      <template v-slot:item.Companies="{ item }">
                        {{ item.Companies.map((c) => c.Name).join(", ") }}
                      </template>
                      <template v-slot:item.Rate="{ item }">
                        <v-chip color="" small>
                          ${{ $consts.TIER_MAP.get(item.Tier).rate }}
                        </v-chip>
                      </template>
                      <template v-slot:item.Buttons="{ item }">
                    <v-btn
                      color="primary"
                      class="ma-2 float-right"
                      plain
                      :to="getReviewLink(item.Id)"
                    >
                      Open
                    </v-btn>
                  </template>
                    </v-data-table>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card>
              <v-card-title class="pb-2 pt-2 text-overline">
                Your past reviews
              </v-card-title>
              <v-card-text>
                <div v-if="pastRevLoading">
                  <v-skeleton-loader type="table-heading"></v-skeleton-loader>
                  <v-skeleton-loader type="list-item"></v-skeleton-loader>
                </div>
                <div v-if="!pastRevLoading">
                  <div v-if="pastRevErrorMsg">
                    <span class="red--text ma-0 sa-0">{{ pastRevErrorMsg }}</span>
                    <v-btn @click="getPastReviews" icon color="primary">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </div>
                  <div v-if="!pastRevErrorMsg">
                    <div
                      v-if="previousItems.length == 0"
                      class="mb-2 d-flex justify-center text-caption"
                    >
                      You don't have any past reviews
                    </div>
                    <v-data-table
                      :headers="computedPreviousHeaders"
                      :items="previousItems"
                      :items-per-page="5"
                      :expanded.sync="pastRevExpanded"
                      item-key="Id"
                      hide-default-footer
                      dense
                      show-expand
                      class="mb-3 elevation-1"
                      v-if="previousItems.length > 0 && !$vuetify.breakpoint.smAndDown"
                    >
                      <template v-slot:item.Tier="{ item }">
                        <v-chip
                            :color="$consts.TIER_MAP.get(item.Tier).color"
                            text-color="white"
                            small
                          >
                          {{ $consts.TIER_MAP.get(item.Tier).name }}
                        </v-chip>
                      </template>
                      <template v-slot:item.LastActionTs="{ item }">
                        {{ $utils.relativeTimeFromTs(item.LastActionTs) }}
                      </template>
                      <template v-slot:item.Status="{ item }">
                        <v-icon color="green" x-small class="mr-1">
                          mdi-circle
                        </v-icon>
                        <span class="d-inline">{{
                          item.Status === "reviewed" ? "Done" : "unknown"
                        }} </span>
                      </template>
                      <template v-slot:item.Companies="{ item }">
                        {{ item.Companies.map((c) => c.Name).join(", ") }}
                      </template>
                      <template v-slot:item.ScoreOverall="{ item }">
                        <v-rating
                          readonly
                          v-model="item.ScoreOverall"
                          background-color="white"
                          color="yellow accent-4"
                          dense
                          half-increments
                          hover
                          size="15"
                        ></v-rating>
                      </template>
                      <template v-slot:expanded-item="{ headers, item }">
                      <td
                        :colspan="headers.length"
                      >
                          {{ item.Id }}
                          <v-btn
                          color="primary"
                          class="ma-2 float-right"
                          plain
                          :to="getReviewLink(item.Id)"
                        >
                          Open
                        </v-btn>
                      </td>
                    </template>
                    </v-data-table>
                  </div>
                </div>
                <v-expansion-panels class="mb-3" v-if="previousItems.length > 0 && $vuetify.breakpoint.smAndDown">
                <v-expansion-panel v-for="item in previousItems" :key="item.id">
                  <v-expansion-panel-header> 
                    <div class=" d-flex align-center">
                        <div class="mr-2">
                            <v-chip
                            :color="$consts.TIER_MAP.get(item.Tier).color"
                            text-color="white"
                            small
                          >
                          {{ $consts.TIER_MAP.get(item.Tier).name }}
                        </v-chip>
                        </div>
                        <div class="mr-2"> {{ $utils.relativeTimeFromTs(item.LastActionTs) }}</div>
                        <div>
                            <v-rating
                            readonly
                            v-model="item.ScoreOverall"
                            background-color="white"
                            color="yellow accent-4"
                            dense
                            half-increments
                            hover
                            size="15"
                            > </v-rating>
                        </div>
                    </div>
                    </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row class="mt-1">
                        <v-col cols="12">
                            <v-chip small v-for="company in item.Companies" :key="company.Id">{{ company.Name }}</v-chip>
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex">
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    class="float-right"
                                    @click="getReviewLink(item.Id)"
                                    plain>
                                    Open
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
                <div class="d-flex" v-if="previousItems.length > 0">
                  <v-spacer></v-spacer>
                  <v-btn plain disabled>Load more</v-btn>
                  <v-spacer></v-spacer>
                </div>
              </v-card-text>
            </v-card>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" lg="4" md="4">
        <v-row>
          <v-col cols="12">
            <v-card elevation="1">
              <v-card-title class="pb-0 pt-2 text-overline">
                Wallet
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" class="d-flex pt-0">
                    <v-spacer></v-spacer>
                    <p class="text-h5 mb-0">$0.0</p>
                  </v-col>
                  <v-col cols="12" class="pb-0 mb-0">
                    <v-btn outlined block> Payment account </v-btn>
                  </v-col>
                  <v-col cols="12" class="pb-0 mb-2">
                    <v-btn outlined block> View your payments </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card>
              <v-card-title class="pb-2 pt-2 text-overline">
                Quick links
              </v-card-title>
              <v-card-text>
                <div class="d-flex flex-column mb-6">
                  <a href="/blah" class="text-decoration-none">
                    Difference between review types
                  </a>
                  <a href="/blah" class="text-decoration-none">
                    Something else here
                  </a>
                  <a href="/blah" class="text-decoration-none">
                    And another link here but longer
                  </a>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  computed: {
    computedAssignedHeaders() {
      return this.assignedHeaders.filter(
        (h) => !h.hide || !this.$vuetify.breakpoint[h.hide]
      );
    },
    computedPreviousHeaders() {
      return this.previousHeaders.filter(
        (h) => !h.hide || !this.$vuetify.breakpoint[h.hide]
      );
    },
  },
  data() {
    return {
      initialLoading: true,
      //reviewerStatus: "active",
      profileLoading: true,
      profileErrorMsg: "",
      expArr: [],
      revProfile: {
        // id: "",
        // name: "Reviewer1612",
        // dateStarted: "2024-05-16",
        // companies: [
        //   { id: "AMZN", name: "Amazon" },
        //   { id: "GOOGL", name: "Google" },
        // ],
        // experience: [
        //   { id: 1, role: "Recruiter", company: "Amazon", workedFor: "2 years" },
        //   {
        //     id: 2,
        //     role: "Hiring Manager",
        //     company: "Google",
        //     workedFor: "3 years",
        //   },
        // ],
      },
      assignedHeaders: [
        { text: "Type", value: "Tier" },
        { text: "Last action", value: "LastActionTs" },
        { text: "Status", value: "Status", hide: "smAndDown" },
        { text: "Target company", value: "Companies", hide: "mdAndDown" },
        { text: "Rate", value: "Rate" },
        { text: "", value: "Buttons" },
      ],
      assignedItems: [],
      previousHeaders: [
        { text: "Type", value: "Tier" },
        { text: "Last action", value: "LastActionTs" },
        { text: "Status", value: "Status", hide: "smAndDown" },
        { text: "Target company", value: "Companies", hide: "mdAndDown" },
        { text: "Overall score", value: "ScoreOverall" },
        { text: "", value: "data-table-expand" },
      ],
      previousItems: [],
      errorMsg: "",
      assignedRevLoading: true,
      assignedRevErrorMsg: "",
      pastRevLoading: true,
      pastRevErrorMsg: "",
      pastRevExpanded: [],
    };
  },
   mounted() {
    this.$utils.visit(this);
    this.getReviewerProfile();
    this.getAssignedReviews();
    this.getPastReviews();
  },
  methods: {
    async getAssignedReviews() {
      try {
        this.assignedRevLoading = true;
        this.assignedRevErrorMsg = "";
        var resp = await this.$utils.callBackendPost(
          this,
          "/rec/getAssignedReviews",
          {}
        );
        this.assignedItems = resp;
      } catch (error) {
        this.assignedRevErrorMsg = error;
      } finally {
        this.assignedRevLoading = false;
      }
    },
    getReviewLink(reviewId) {
      return "/review?id=" + reviewId;
    },
    async getPastReviews() {
      try {
        this.pastRevLoading = true;
        this.pastRevErrorMsg = "";
        var resp = await this.$utils.callBackendPost(
          this,
          "/rec/getArchivedReviews",
          {}
        );
        this.previousItems = resp;
      } catch (error) {
        this.pastRevErrorMsg = error;
      } finally {
        this.pastRevLoading = false;
      }
    },
    async getReviewerProfile(revId) {
      this.profileLoading = true;
      this.profileErrorMsg = "";
      try {
        var resp = await this.$utils.callBackendGet(
          this,
          `/pub/getReviewerPublicProfile?email=${this.$store.getters.getUser}`
        );
        this.revProfile = resp;
        // todo: fix this mess... We parse experience because we store it in db as json which is lazy and not great.
        if (this.revProfile.Experience) {
          var exp = JSON.parse(this.revProfile.Experience);
          this.expArr = [];
          var ctr = 0;
          for (var i = 0; i < exp.length; i++) {
            for (var j = 0; j < exp[i].selRoles.length; j++) {
              this.expArr.push({
                id: ctr,
                company: exp[i].selCompany,
                role: exp[i].selRoles[j],
              });
              ctr++;
            }
          }
        }
        console.log("revProfile: " + JSON.stringify(this.revProfile));
      } catch (error) {
        this.profileErrorMsg = error;
      } finally {
        this.profileLoading = false;
      }
    },
  },
};
</script>
